import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import ModalComponent from 'components/common/ModalComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const exportColumns = [
  { key: 'workerName', label: 'Worker' }, { key: 'date', label: 'Date' },
  { key: 'startTime', label: 'Start Time' }, { key: 'endTime', label: 'End Time' },
  { key: 'edited', label: 'Edited' }
]

export default function TimingDataEdit () {
  const hasFetched = useRef(false)
  const currentFilter = useRef(null)
  const { user } = useAuth()
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)
  const [state, setState] = useState({
    workdays: [],
    searchTerm: '',
    showEditModal: false,
    selectedWorkday: null,
    loading: true
  })

  const getWorkdays = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/workdays/${user.farmId}?${buildDashboardDateRangeQuery(dateRange)}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, workdays: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const openEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedWorkday: row }))
  }

  const closeEditModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedWorkday: null }))
  }

  const handleSaveWorkday = async (formData) => {
    try {
      await makeRequest({
        method: 'PATCH',
        url: `/management/workdays/${state.selectedWorkday.workdayId}`,
        data: {
          startTime: formData.startTime || undefined,
          endTime: formData.endTime || undefined
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: 'Workday updated', icon: 'success' })
      closeEditModal()
      await getWorkdays()
    } catch (error) {
      showAlert({ text: 'Failed to update workday', icon: 'error' })
      console.error('Error updating workday:', error)
    }
  }

  const filteredWorkdays = filterRowsBySearch(state.workdays, state.searchTerm)

  useEffect(() => {
    if (!user?.farmId) return
    const nextFilter = JSON.stringify(dateRange)
    if (hasFetched.current && currentFilter.current === nextFilter) return
    hasFetched.current = true
    currentFilter.current = nextFilter
    getWorkdays()
  }, [user?.farmId, dateRange])

  if (state.loading) {
    return <PageLoadingState message='Loading timing data...' rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>{getDateRangeSummary(dateRange)}</span>
          </div>
        </div>
        <div className='flex items-center gap-4 mb-6'>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditExcel({ rows: filteredWorkdays, columns: exportColumns, fileName: `timing-data-${currentDate}`, sheetName: 'Timing Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as Excel</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditPdf({ rows: filteredWorkdays, columns: exportColumns, fileName: `timing-data-${currentDate}`, title: 'Timing Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as PDF</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder='Search all fields...'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent className='flex items-center gap-2' onClick={() => setShowFilters(true)}><FunnelIcon className='w-5 h-5' /><span>Advanced filters</span></ButtonComponent>
      </div>

      <div className='data-table'>
        <TableComponent
          tableData={{
            columns: [
              { key: 'workerName', label: 'Worker' },
              { key: 'date', label: 'Date', cell: (value) => parseDate(value) },
              { key: 'startTime', label: 'Start Time' },
              { key: 'endTime', label: 'End Time' },
              { key: 'edited', label: 'Edited', cell: (value) => value ? 'Yes' : 'No' },
              { key: 'actions', label: 'Manage' }
            ],
            rows: filteredWorkdays
          }}
          handleEdit={openEditModal}
          defaultSortColumn='workerName'
        />
      </div>

      {state.showEditModal && (
        <ModalComponent
          title='Edit Attendance Record'
          isOpen={state.showEditModal}
          onClose={closeEditModal}
          onSave={handleSaveWorkday}
          initialData={{
            startTime: state.selectedWorkday?.startTime ?? '',
            endTime: state.selectedWorkday?.endTime ?? ''
          }}
        >
          {({ formData, handleChange }) => (
            <form className='space-y-4 w-64' autoComplete='off'>
              <p className='text-sm text-gray-600'>
                Worker: <strong>{state.selectedWorkday?.workerName}</strong>
              </p>
              <label className='block text-sm font-medium'>
                Start Time
                <input
                  type='time'
                  name='startTime'
                  value={formData?.startTime ?? ''}
                  onChange={handleChange}
                  className='w-full mt-1 p-2 border rounded'
                />
              </label>
              <label className='block text-sm font-medium'>
                End Time
                <input
                  type='time'
                  name='endTime'
                  value={formData?.endTime ?? ''}
                  onChange={handleChange}
                  className='w-full mt-1 p-2 border rounded'
                />
              </label>
            </form>
          )}
        </ModalComponent>
      )}
      <DateRangeFilterModal isOpen={showFilters} dateRange={dateRange} sectors={filterOptions.sectors} farmingBlocks={filterOptions.farmingBlocks} groups={filterOptions.groups} activities={filterOptions.activities} maxDate={currentDate} onClose={() => setShowFilters(false)} onApply={(nextRange) => { setDateRange(nextRange); setShowFilters(false) }} />
    </div>
  )
}
